// Every incident a driver has been involved in or held responsible for,
// whatever its fault classification or claim type -- protection-only
// (Section 2) and not-at-fault claims included. Repeated incidents matter for
// suitability and risk decisions even when fault or recovery differs, so this
// is deliberately not filtered down to at-fault claims or to charges.
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Download, RefreshCw } from "lucide-react";
import { loadDriverHistory, type HistoryEntry } from "@/lib/incidentResponsibility";
import { useAppContext } from "@/contexts/AppContext";
import { money, round2 } from "@/lib/driverDeductions";
import { formatDate } from "@/lib/dates";

const FAULT_LABEL: Record<string, string> = {
  not_at_fault: "Not at fault",
  at_fault: "At fault",
  section2: "Section 2 (protection only)",
  section2_with_repair_cost: "Section 2 + repair cost",
  repair_internally: "Repaired internally",
};
const CATEGORY_LABEL: Record<string, string> = {
  vehicle_collision: "Vehicle collision",
  property_damage: "Property damage",
  single_vehicle: "Single vehicle",
  other: "Other",
};
const CLAIM_LABEL: Record<string, string> = {
  full: "Full claim",
  section2: "Section 2",
  third_party_at_fault: "Third party at fault",
};

const csvCell = (v: unknown) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

interface Props {
  onOpenIncident?: (incidentId: string) => void;
}

const InsuranceDriverHistoryTab: React.FC<Props> = ({ onOpenIncident }) => {
  // A restricted team member only ever sees their linked driver(s), same as
  // the Incidents and Claims tabs. Unrestricted (the default) sees everyone.
  const { effectivePermissions } = useAppContext();
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;

  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const all = await loadDriverHistory();
      setEntries(scopedDriverNames ? all.filter((e) => scopedDriverNames.includes(e.driverName)) : all);
    } catch (e) {
      toast({ title: "Couldn't load claim history", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const driverNames = useMemo(() => [...new Set(entries.map((e) => e.driverName))].sort((a, b) => a.localeCompare(b)), [entries]);
  const shown = useMemo(() => (driver ? entries.filter((e) => e.driverName === driver) : entries), [entries, driver]);

  const summary = useMemo(() => {
    const incidents = new Set(shown.map((e) => e.incidentId)).size;
    return {
      incidents,
      atFault: shown.filter((e) => e.faultClassification === "at_fault").length,
      protectionOnly: shown.filter((e) => e.faultClassification === "section2").length,
      charged: round2(shown.reduce((s, e) => s + (e.allocated ?? 0), 0)),
      paid: round2(shown.reduce((s, e) => s + e.paid, 0)),
      owed: round2(shown.reduce((s, e) => s + (e.remaining ?? 0), 0)),
    };
  }, [shown]);

  const exportCsv = () => {
    const header = ["Date", "Driver", "Role", "Vehicle", "Incident", "Fault", "Claim", "Claim status", "Excess share", "Paid", "Still owed", "Charge status", "Cycle restarted"];
    const lines = shown.map((e) =>
      [
        e.incidentDate, e.driverName, e.role === "driver" ? "Driver" : "Responsible", e.vehicleNumber ?? "",
        CATEGORY_LABEL[e.category] ?? e.category, e.faultClassification ? FAULT_LABEL[e.faultClassification] : "Not classified",
        e.claimType ? CLAIM_LABEL[e.claimType] ?? e.claimType : "", e.claimStatus ?? "",
        e.allocated === null ? "" : e.allocated.toFixed(2), e.paid.toFixed(2), e.remaining === null ? "" : e.remaining.toFixed(2),
        e.deductionStatus ?? "", e.restartedCycle ? "Yes" : "",
      ].map(csvCell).join(",")
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `driver-claim-history${driver ? `_${driver.replace(/\s+/g, "-")}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl text-primary">Driver Claim History</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={shown.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Every incident a driver has been on or held responsible for -- protection-only (Section 2) and not-at-fault
          claims included, whatever the recovery. Kept even after a driver is deactivated.
        </p>

        <div className="max-w-xs">
          <Label className="text-xs">Driver</Label>
          <select
            className="dark-input flex h-9 w-full rounded-md border px-2 text-sm"
            value={driver}
            onChange={(e) => setDriver(e.target.value)}
          >
            <option value="">All drivers</option>
            {driverNames.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline">{summary.incidents} incident{summary.incidents === 1 ? "" : "s"}</Badge>
          <Badge variant="outline" className="border-red-400/50 text-red-400">{summary.atFault} at fault</Badge>
          <Badge variant="outline" className="border-blue-400/50 text-blue-400">{summary.protectionOnly} protection only</Badge>
          <Badge variant="outline">Excess charged {money(summary.charged)}</Badge>
          <Badge variant="outline">Paid {money(summary.paid)}</Badge>
          <Badge variant="outline" className="border-yellow-500/50 text-yellow-500">Still owed {money(summary.owed)}</Badge>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : shown.length === 0 ? (
          <p className="text-sm text-muted-foreground">No incidents on record{driver ? ` for ${driver}` : ""}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-1.5 pr-3">Date</th>
                  <th className="py-1.5 pr-3">Driver</th>
                  <th className="py-1.5 pr-3">Vehicle</th>
                  <th className="py-1.5 pr-3">Incident</th>
                  <th className="py-1.5 pr-3">Fault</th>
                  <th className="py-1.5 pr-3">Claim</th>
                  <th className="py-1.5 pr-3 text-right">Excess share</th>
                  <th className="py-1.5 pr-3 text-right">Paid</th>
                  <th className="py-1.5 pr-3 text-right">Still owed</th>
                  <th className="py-1.5">Cycle</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((e) => (
                  <tr
                    key={e.key}
                    className={`border-b border-border/40 ${onOpenIncident ? "cursor-pointer hover:bg-muted/30" : ""}`}
                    onClick={() => onOpenIncident?.(e.incidentId)}
                  >
                    <td className="py-1.5 pr-3 whitespace-nowrap">{formatDate(e.incidentDate)}</td>
                    <td className="py-1.5 pr-3">
                      {e.driverName}
                      {e.role === "responsible" && <span className="ml-1 text-[10px] text-muted-foreground">(responsible)</span>}
                    </td>
                    <td className="py-1.5 pr-3">{e.vehicleNumber ? `#${e.vehicleNumber}` : "—"}</td>
                    <td className="py-1.5 pr-3">{CATEGORY_LABEL[e.category] ?? e.category}</td>
                    <td className="py-1.5 pr-3">{e.faultClassification ? FAULT_LABEL[e.faultClassification] : <span className="text-muted-foreground">Not classified</span>}</td>
                    <td className="py-1.5 pr-3">
                      {e.claimType ? `${CLAIM_LABEL[e.claimType] ?? e.claimType}${e.claimStatus ? ` · ${e.claimStatus.replace(/_/g, " ")}` : ""}` : <span className="text-muted-foreground">No claim</span>}
                    </td>
                    <td className="py-1.5 pr-3 text-right">{e.allocated === null ? "—" : money(e.allocated)}</td>
                    <td className="py-1.5 pr-3 text-right">{e.allocated === null ? "—" : money(e.paid)}</td>
                    <td className="py-1.5 pr-3 text-right font-medium">{e.remaining === null ? "—" : money(e.remaining)}</td>
                    <td className="py-1.5">{e.restartedCycle ? <Badge variant="outline" className="text-[10px] border-yellow-500/50 text-yellow-500">restarted</Badge> : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InsuranceDriverHistoryTab;
